/**
 * The seven columns of Agenda semanal.
 *
 * available_slots holds only the times a professional's working hours generate.
 * A session booked off that grid — on a closed Saturday, at 20:00, at 9:15 on a
 * grid that steps 9:00, 9:45 — had no slot of its own: on a closed day the
 * column said "Sin horarios" over a session that existed, and an off-grid one
 * showed as the two slots it overlapped. So on their own agenda a professional's
 * sessions are drawn from the sessions themselves, at their real times, and the
 * grid adds what the sessions cannot say: which times are free and which are
 * blocked. A slot a listed session overlaps is left out, since that session is
 * already on screen.
 *
 * A patient's reading passes their own family's sessions with that professional
 * (my_sessions), so they can cancel one from the week (0057). The grid itself
 * never names who holds a slot for them.
 */

import { addDaysToKey, dayKey } from './practiceTime';

const at = (instant) => new Date(instant).getTime();

/**
 * @param weekStart  Monday, as a day key.
 * @param slots      Rows from available_slots for that week.
 * @param sessions   The active sessions the viewer may see that week: a
 *                   professional's own, or a patient's family's; null for none.
 * @param now        For tests; a slot already started is not offered.
 * @returns [{ key, entries: [{ kind: 'slot', at, slot } | { kind: 'session', at, session }] }]
 */
export const weekColumns = ({ weekStart, slots = [], sessions = null, now = Date.now() }) => {
  const overlapsASession = (slot) => (sessions ?? []).some((session) =>
    at(session.starts_at) < at(slot.slot_end) && at(session.ends_at) > at(slot.slot_start));

  return Array.from({ length: 7 }, (_, index) => {
    const key = addDaysToKey(weekStart, index);

    // A slot whose time has passed is not offered, even today.
    const offered = slots
      .filter((slot) => dayKey(slot.slot_start) === key && at(slot.slot_start) > now)
      .filter((slot) => !(slot.busy_reason === 'appointment' && overlapsASession(slot)))
      .map((slot) => ({ kind: 'slot', at: slot.slot_start, slot }));

    // A session under way stays until it ends.
    const booked = (sessions ?? [])
      .filter((session) => dayKey(session.starts_at) === key && at(session.ends_at) > now)
      .map((session) => ({ kind: 'session', at: session.starts_at, session }));

    return { key, entries: [...offered, ...booked].sort((a, b) => at(a.at) - at(b.at)) };
  });
};
